import { useState, useEffect } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6nflg94a3uojp5fh5og';

export default function Jessica() {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [waitlistPosition, setWaitlistPosition] = useState<number | null>(null);
  const [signupResult, setSignupResult] = useState<{
    position: number | null;
    cohortMonth: string | null;
    cohortPosition: number | null;
    isCurrentMonth: boolean;
    spotsRemaining: number | null;
    message: string | null;
  } | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [liveSpots, setLiveSpots] = useState<number>(75);
  const [liveCohortMonth, setLiveCohortMonth] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    use_case: '',
    referral_source: ''
  });

  useEffect(() => {
    if (!showShareDropdown) return;
    const handler = () => setShowShareDropdown(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showShareDropdown]);

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.use_case.length > 500) return;
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) formBody.append(key, value);
      });
      const readyResponse = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });

      const signupPayload = {
        email: formData.email,
        firstName: formData.first_name,
        businessName: '',
        industry: '',
        notify_referrer: false,
        referred_by: '',
        new_signup_first_name: formData.first_name,
        new_signup_last_name: formData.last_name,
        new_signup_email: formData.email,
        coupon_code: '',
        utm_source: 'jessica.petergits.com',
        utm_campaign: 'launch2026'
      };

      let parsedResult: typeof signupResult = null;

      try {
        const signupResponse = await fetch(
          'https://staging.adskipper.petergits.com/api/jessica-signup/signup',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(signupPayload)
          }
        );
        if (signupResponse.ok) {
          const data = await signupResponse.json();
          parsedResult = {
            position: data?.position ?? data?.data?.position ?? null,
            cohortMonth: data?.cohortMonth ?? data?.data?.cohortMonth ?? null,
            cohortPosition: data?.cohortPosition ?? data?.data?.cohortPosition ?? null,
            isCurrentMonth: data?.isCurrentMonth ?? data?.data?.isCurrentMonth ?? true,
            spotsRemaining: data?.spotsRemaining ?? data?.data?.spotsRemaining ?? null,
            message: data?.message ?? data?.data?.message ?? null,
          };
          // Update live spots counter if API returns it
          if (parsedResult.spotsRemaining !== null && parsedResult.isCurrentMonth) {
            setLiveSpots(parsedResult.spotsRemaining);
          }
        }
      } catch (signupErr) {
        console.error('Jessica signup API error:', signupErr);
      }

      if (readyResponse.ok) {
        setWaitlistPosition(parsedResult?.position ?? null);
        setSignupResult(parsedResult);
        setFormSubmitted(true);
        setFormData({ first_name: '', last_name: '', email: '', phone: '', use_case: '', referral_source: '' });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleShareCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText('https://petergits.com/jessica').then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  };

  const scrollToForm = () => document.getElementById('get-jessica')?.scrollIntoView({ behavior: 'smooth' });
  const scrollToDemo = () => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' });
  const scrollToProof = () => document.getElementById('proof')?.scrollIntoView({ behavior: 'smooth' });

  /* ── Schema.org JSON-LD ── */
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://jessica.readdy.co';

    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'GITS',
      url: 'https://petergits.com',
      logo: 'https://petergits.com/logo.png',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+17433005858',
        contactType: 'customer service',
        areaServed: 'US',
        availableLanguage: 'English',
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Charlotte',
        addressRegion: 'NC',
        addressCountry: 'US',
      },
      sameAs: ['https://petergits.com'],
    };

    const landingPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Jessica AI Voice Agent – Free 30-Day Trial | Books Calls 24/7 | GITS',
      description:
        'Get your own AI voice agent (Jessica) free for 30 days — books appointments, handles cancellations, answers calls 24/7. No credit card. No commitment. Limited spots per cohort.',
      url: siteUrl,
      inLanguage: 'en-US',
      isPartOf: {
        '@type': 'WebSite',
        name: 'Jessica by GITS',
        url: siteUrl,
      },
      publisher: {
        '@type': 'Organization',
        name: 'GITS',
        url: 'https://petergits.com',
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: siteUrl,
          },
        ],
      },
    };

    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Jessica AI Voice Agent',
      alternateName: 'Jessica by GITS',
      description:
        'A 24/7 AI voice agent that books appointments, handles cancellations, answers inbound calls, and integrates with your calendar — free 30-day trial, no credit card required.',
      provider: {
        '@type': 'Organization',
        name: 'GITS',
        url: 'https://petergits.com',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Charlotte',
          addressRegion: 'NC',
          addressCountry: 'US',
        },
      },
      areaServed: {
        '@type': 'Country',
        name: 'United States',
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Jessica AI Voice Agent Plans',
        itemListElement: [
          {
            '@type': 'Offer',
            name: '30-Day Free Trial',
            price: '0',
            priceCurrency: 'USD',
            description: '30-day free trial — no credit card, no commitment. Limited to 100 spots per cohort.',
            availability: 'https://schema.org/LimitedAvailability',
            validFrom: '2025-01-01',
          },
        ],
      },
      telephone: '+17433005858',
      url: siteUrl,
      serviceType: 'AI Voice Agent',
      category: 'Artificial Intelligence',
    };

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Do I need a business to get my own Jessica?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Absolutely not. You don't need a business, a website, or even a business idea. If you're just curious about how AI voice agents work and want to kick the tires — that's more than enough reason. Think of it as a no-strings test drive. We set her up, you play around, no commitment required.",
          },
        },
        {
          '@type': 'Question',
          name: 'What calendar does Jessica connect to?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Jessica connects to Google Calendar and can work with most major calendar platforms. During your setup, our team configures the integration for you — you don't touch a single line of code. She'll book, reschedule, and cancel appointments directly to your calendar in real time.",
          },
        },
        {
          '@type': 'Question',
          name: 'What happens after the free trial?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Nothing happens automatically — we don't store your card, and there's no auto-renewal. After your trial, if you love Jessica and want to keep her, we'll talk about what a paid plan looks like for your situation. If you don't want to continue, you just walk away. Zero pressure, zero awkward cancellation calls.",
          },
        },
        {
          '@type': 'Question',
          name: 'How long does setup actually take?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "We target 48 hours from the moment you're confirmed on the waitlist. Our team handles the voice training, calendar integration, and call flow configuration. You'll get a notification when she's live, along with a dedicated phone number to test her out.",
          },
        },
        {
          '@type': 'Question',
          name: 'Can Jessica handle cancellations and reschedules — not just bookings?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Yes — that's actually one of the best things to test. Call her, book an appointment, then call back and cancel it. She handles the full lifecycle: booking, confirmation, rescheduling, and cancellation. That's exactly what the demo is designed to show you.",
          },
        },
        {
          '@type': 'Question',
          name: "What if I don't get a spot this month?",
          acceptedAnswer: {
            '@type': 'Answer',
            text: "We can only onboard 3–4 accounts per day, so some months fill up fast. If we can't get you in this month, you automatically go to the front of the line for next month. We'll email you to confirm your priority spot — you won't lose your place.",
          },
        },
        {
          '@type': 'Question',
          name: "Is this really free? What's the catch?",
          acceptedAnswer: {
            '@type': 'Answer',
            text: "It's genuinely free. No credit card, no hidden fees, no \"free trial that converts to $299/month without warning.\" The catch — if you can call it that — is that we hope you love it enough to become a paying customer. But that's entirely your call, after you've seen it work.",
          },
        },
      ],
    };

    const schemas = [organizationSchema, landingPageSchema, serviceSchema, faqSchema];
    const scriptIds = ['schema-organization', 'schema-webpage', 'schema-service', 'schema-faq'];

    schemas.forEach((schema, i) => {
      const existing = document.getElementById(scriptIds[i]);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.id = scriptIds[i];
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      scriptIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  useEffect(() => {
    fetch('https://staging.adskipper.petergits.com/api/jessica-signup/stats')
      .then((res) => res.json())
      .then((data) => {
        if (data?.success && data?.stats?.spotsRemaining != null) {
          setLiveSpots(data.stats.spotsRemaining);
        }
        if (data?.success && data?.stats?.cohortMonth) {
          setLiveCohortMonth(data.stats.cohortMonth);
        }
      })
      .catch(() => {
        // silently fall back to default
      });
  }, []);

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">

      {/* Top Announcement Banner */}
      {!bannerDismissed && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-[#FF3366] via-[#cc2952] to-[#FF3366] border-b border-[#FF3366]/60 shadow-[0_2px_20px_rgba(255,51,102,0.4)]">
          <div className="flex items-center justify-center gap-3 px-4 py-2.5 flex-wrap relative">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse shrink-0"></span>
            {liveSpots === 0 ? (
              <p className="text-white text-sm font-bold text-center leading-tight">
                <span className="font-black text-base">This month is full!</span>
                {liveCohortMonth
                  ? <> Signups now queue for <span className="font-black">{liveCohortMonth}</span> —{' '}</>
                  : <> Next cohort spots are open —{' '}</>}
                <a href="#get-jessica" onClick={scrollToForm} className="underline underline-offset-2 hover:text-[#FFE566] transition-colors whitespace-nowrap cursor-pointer font-extrabold">
                  Reserve your spot →
                </a>
              </p>
            ) : (
              <p className="text-white text-sm font-bold text-center leading-tight">
                <span className="font-black text-base">{liveSpots}</span> of 100 free trial spots remaining{liveCohortMonth ? <span className="font-normal opacity-90"> for <span className="font-black">{liveCohortMonth}</span></span> : ''} —{' '}
                <a href="#get-jessica" onClick={scrollToForm} className="underline underline-offset-2 hover:text-[#FFE566] transition-colors whitespace-nowrap cursor-pointer font-extrabold">
                  Claim yours before they fill up →
                </a>
              </p>
            )}
            <span className="w-2 h-2 bg-white rounded-full animate-pulse shrink-0"></span>
            <button
              onClick={() => setBannerDismissed(true)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 rounded-full transition-all duration-200 cursor-pointer"
              aria-label="Dismiss"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>
        </div>
      )}
      <div className={bannerDismissed ? '' : 'h-10'}></div>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center relative px-6 py-8">
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-[#00FF94]/5 to-transparent pointer-events-none"></div>
        <div className="max-w-[1400px] mx-auto w-full relative z-10">
          <div className="max-w-[1000px]">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-[#FF3366]/10 border-2 border-[#FF3366] rounded-full text-[#FF3366] text-sm font-semibold uppercase tracking-wider mb-12">
              <span className="text-xl">⚠</span>
              <span>Reality Check</span>
            </div>
            <h1 className="font-black text-7xl md:text-8xl lg:text-9xl leading-none uppercase tracking-tight mb-8">
              Put Your{' '}
              <span className="text-[#00FF94] relative inline-block">
                Money
                <span className="absolute bottom-0 left-0 right-0 h-1 bg-[#00FF94]"></span>
              </span>
              <br />Where Your<br />
              <span className="text-[#00FF94] relative inline-block">
                Calls
                <span className="absolute bottom-0 left-0 right-0 h-1 bg-[#00FF94]"></span>
              </span>{' '}Are
            </h1>
            <p className="text-2xl md:text-3xl font-light text-gray-400 mb-12 max-w-[800px]">
              Your customers are calling. Your competitors are answering.{' '}
              <strong className="text-white font-extrabold">With AI.</strong>
            </p>
            <div className="bg-gradient-to-br from-[#00FF94]/10 to-[#0066FF]/10 border-l-4 border-[#00FF94] p-10 mb-12 max-w-[900px]">
              <p className="text-2xl font-semibold leading-relaxed">
                Every missed call is $500-$2,000 walking out the door. Every hold time is a customer googling your competitor. Every &quot;press 1 for...&quot; is a conversion dying in real-time.
              </p>
            </div>
            <div className="flex flex-wrap gap-6 items-center">
              <a href="#get-jessica" onClick={(e) => { e.preventDefault(); scrollToDemo(); }} className="px-14 py-6 bg-[#00FF94] text-[#0D0D0D] text-lg font-extrabold uppercase tracking-wide hover:bg-white transition-all duration-300 shadow-[0_0_30px_rgba(0,255,148,0.3)] hover:shadow-[0_0_50px_rgba(0,255,148,0.5)] hover:-translate-y-0.5 whitespace-nowrap cursor-pointer inline-block">
                See It Work →
              </a>
              <a href="#proof" onClick={(e) => { e.preventDefault(); scrollToProof(); }} className="px-14 py-6 bg-transparent text-white text-lg font-extrabold uppercase tracking-wide border-2 border-white hover:bg-white hover:text-[#0D0D0D] transition-all duration-300 whitespace-nowrap cursor-pointer inline-block">
                Show Me Proof
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Brutal Truth Section */}
      <section className="py-24 px-6 bg-[#1A1A1A]">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black uppercase mb-6">The <span className="text-[#FF3366]">Brutal</span> Truth</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">While you&apos;re &quot;considering&quot; AI voice agents, here&apos;s what&apos;s happening right now:</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              { icon: 'ri-phone-line', stat: '67%', desc: 'of callers hang up after 2 minutes on hold' },
              { icon: 'ri-money-dollar-circle-line', stat: '$1,200', desc: 'average value of a missed service call' },
              { icon: 'ri-time-line', stat: '24/7', desc: "your competitors' AI agents are working" },
            ].map((item, idx) => (
              <div key={idx} className="bg-[#0D0D0D] border-2 border-gray-800 p-10 hover:border-[#FF3366] transition-all duration-300 flex flex-col items-start gap-6">
                <div className="w-14 h-14 flex items-center justify-center bg-[#FF3366]/10 rounded-xl shrink-0">
                  <i className={`${item.icon} text-2xl text-[#FF3366]`}></i>
                </div>
                <div>
                  <p className="text-6xl font-black text-white leading-none mb-3">{item.stat}</p>
                  <p className="text-gray-400 text-base">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-[#FF3366]/20 to-[#FF3366]/5 border-l-4 border-[#FF3366] p-10 max-w-4xl mx-auto">
            <p className="text-2xl font-semibold text-center">
              Your phone system isn&apos;t just outdated. <span className="text-[#FF3366] font-black">It&apos;s actively costing you customers.</span>
            </p>
          </div>
        </div>
      </section>

      {/* AI Voice Agent Section */}
      <section className="py-24 px-6 bg-[#0D0D0D]">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00FF94]/10 text-[#00FF94] rounded-full text-sm font-bold uppercase tracking-widest mb-6">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-robot-line"></i></span>
              The Solution
            </div>
            <h2 className="text-5xl md:text-6xl font-black uppercase mb-6">AI Voice Agent That <span className="text-[#00FF94]">Actually Works</span></h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">Not a chatbot. Not an IVR. A real conversation that books appointments, answers questions, and closes deals.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div className="bg-[#1A1A1A] border-2 border-[#FF3366]/30 p-10">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 flex items-center justify-center bg-[#FF3366]/20 rounded-xl">
                  <i className="ri-close-circle-line text-3xl text-[#FF3366]"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-black text-[#FF3366]">Your Current System</h3>
                  <p className="text-gray-500">Losing money daily</p>
                </div>
              </div>
              <ul className="space-y-4">
                {['Calls go to voicemail after hours','Customers wait 8+ minutes on hold','Staff overwhelmed during peak times','No way to track missed opportunities','Same questions answered 100x/day','Zero scalability without hiring'].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-6 h-6 flex items-center justify-center text-[#FF3366] mt-1"><i className="ri-close-line text-xl"></i></span>
                    <span className="text-gray-400 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-[#00FF94]/10 to-[#0066FF]/10 border-2 border-[#00FF94] p-10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#00FF94]/10 rounded-full blur-3xl"></div>
              <div className="flex items-center gap-4 mb-8 relative">
                <div className="w-14 h-14 flex items-center justify-center bg-[#00FF94]/20 rounded-xl">
                  <i className="ri-checkbox-circle-line text-3xl text-[#00FF94]"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-black text-[#00FF94]">With Jessica</h3>
                  <p className="text-gray-400">Making money 24/7</p>
                </div>
              </div>
              <ul className="space-y-4 relative">
                {['Answer every call, instantly, 24/7/365','Zero hold time, ever','Handle unlimited concurrent calls','Full conversation analytics & insights','Consistent, accurate answers every time','Scale infinitely with zero hiring'].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-6 h-6 flex items-center justify-center text-[#00FF94] mt-1"><i className="ri-check-line text-xl"></i></span>
                    <span className="text-white text-lg font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Do The Math Section */}
      <section id="proof" className="py-24 px-6 bg-[#1A1A1A]">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-5xl md:text-7xl font-black uppercase mb-6">Do The <span className="text-[#00FF94]">Math</span></h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">Conservative estimates. Real numbers. Your money.</p>
          </div>
          <div className="bg-[#0D0D0D] border-2 border-[#00FF94] p-12">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-bold mb-6 text-[#FF3366]">Current Costs:</h3>
                <div className="space-y-4">
                  {[['Missed calls per week','20-50'],['Average call value','$800'],['Monthly lost revenue','$64,000+'],['Annual lost revenue','$768,000+']].map(([label, val]) => (
                    <div key={label} className="flex justify-between items-center py-3 border-b border-gray-800">
                      <span className="text-gray-400">{label}</span>
                      <span className="text-2xl font-bold text-white">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-6 text-[#00FF94]">With AI Voice Agent:</h3>
                <div className="space-y-4">
                  {[['Calls answered','100%'],['Monthly recovered revenue','$64,000+'],['AI agent monthly cost','$2,500'],['Net monthly gain','$61,500+']].map(([label, val]) => (
                    <div key={label} className="flex justify-between items-center py-3 border-b border-gray-800">
                      <span className="text-gray-400">{label}</span>
                      <span className="text-2xl font-bold text-[#00FF94]">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-[#00FF94]/20 to-[#0066FF]/20 border-2 border-[#00FF94] p-8 text-center mt-8">
              <p className="text-3xl font-black mb-2">ROI: <span className="text-[#00FF94]">2,460%</span></p>
              <p className="text-lg text-gray-400">Pays for itself in the first 3 calls</p>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" className="py-24 px-6 bg-[#0D0D0D]">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black uppercase mb-6">See Jessica <span className="text-[#00FF94]">In Action</span></h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">Call our demo line right now. Experience what your customers could be experiencing.</p>
            <p className="text-lg text-[#00FF94] font-semibold max-w-2xl mx-auto mt-4">Ask her to role play with you for your business. She is way more capable than you think.</p>
          </div>
          <div className="bg-gradient-to-br from-[#00FF94]/10 to-[#0066FF]/10 border-2 border-[#00FF94] p-12 text-center">
            <div className="mb-8">
              <a href="tel:+17433005858" className="w-24 h-24 flex items-center justify-center bg-[#00FF94] rounded-full mx-auto mb-6 hover:bg-white transition-colors cursor-pointer">
                <i className="ri-phone-line text-5xl text-[#0D0D0D]"></i>
              </a>
              <p className="text-2xl font-semibold text-gray-400 mb-4">Call Now:</p>
              <a href="tel:+17433005858" className="text-6xl font-black text-[#00FF94] hover:text-white transition-colors cursor-pointer">(743) 300-5858</a>
            </div>
            <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto leading-relaxed">Try to stump Jessica. Ask her complex questions. See how she handles objections. Take her for a test drive now.</p>
            <div className="flex justify-center">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-[#00FF94]/20 text-[#00FF94] rounded-full text-sm font-semibold">
                <span className="w-3 h-3 bg-[#00FF94] rounded-full animate-pulse"></span>
                Live Demo Available 24/7
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Get Your Own Jessica Section */}
      <section id="get-jessica" className="py-28 px-6 bg-[#0D0D0D] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#00FF94]/5 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-[#00FF94]/10 border border-[#00FF94]/40 rounded-full text-[#00FF94] text-sm font-bold uppercase tracking-widest mb-8">
              <span className="w-2 h-2 bg-[#00FF94] rounded-full animate-pulse"></span>
              Limited — 100 Free Trials This Month
            </div>
            <h2 className="text-5xl md:text-7xl font-black uppercase leading-none mb-6">
              Get Your Own<br /><span className="text-[#00FF94]">Jessica</span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              No business required. No strings attached. Just a free test-drive of your very own AI voice agent — set up, trained, and ready to book calls to your calendar.
            </p>
          </div>

          {/* Availability counter */}
          <div className={`border-2 rounded-2xl p-6 mb-16 text-center max-w-3xl mx-auto ${liveSpots === 0 ? 'bg-gradient-to-r from-amber-500/20 via-amber-500/10 to-amber-500/20 border-amber-500/60' : 'bg-gradient-to-r from-[#FF3366]/20 via-[#FF3366]/10 to-[#FF3366]/20 border-[#FF3366]/60'}`}>
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className={`w-3 h-3 rounded-full animate-pulse shrink-0 ${liveSpots === 0 ? 'bg-amber-400' : 'bg-[#FF3366]'}`}></span>
              <span className={`text-xs font-bold uppercase tracking-widest ${liveSpots === 0 ? 'text-amber-400' : 'text-[#FF3366]'}`}>
                {liveSpots === 0 ? 'Next Cohort' : 'Live Availability'}
              </span>
              <span className={`w-3 h-3 rounded-full animate-pulse shrink-0 ${liveSpots === 0 ? 'bg-amber-400' : 'bg-[#FF3366]'}`}></span>
            </div>

            {liveSpots === 0 ? (
              <>
                <div className="flex items-center justify-center gap-3 mb-3">
                  <div className="w-14 h-14 flex items-center justify-center bg-amber-400/20 rounded-full">
                    <i className="ri-calendar-event-line text-3xl text-amber-400"></i>
                  </div>
                  <div className="text-left">
                    <p className="text-amber-400 font-black text-xl leading-tight">This month is full</p>
                    <p className="text-white font-bold text-sm leading-tight">0 of 100 spots remaining</p>
                  </div>
                </div>
                <div className="w-full max-w-sm mx-auto bg-gray-800 rounded-full h-3 mb-4 overflow-hidden">
                  <div className="h-3 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 w-full"></div>
                </div>
                <p className="text-white text-base font-bold leading-relaxed">
                  <span className="text-amber-400 font-black">This month&apos;s 100 slots are taken.</span> Sign up below and you&apos;ll be first in line for the next cohort.
                </p>
                <p className="text-gray-400 text-sm mt-2">
                  We&apos;ll email you to confirm your <strong className="text-white">priority spot</strong> — you won&apos;t lose your place.
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center gap-4 mb-3">
                  <div className="text-5xl font-black tabular-nums transition-all duration-300">{liveSpots}</div>
                  <div className="text-left">
                    <p className="text-white font-bold text-lg leading-tight">of 100 spots</p>
                    <p className="text-[#FF3366] font-bold text-lg leading-tight">remaining</p>
                  </div>
                </div>
                {liveCohortMonth && (
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 border border-white/10 rounded-full mb-3">
                    <span className="w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-calendar-2-line text-sm"></i>
                    </span>
                    <span className="text-gray-300 text-sm font-semibold">{liveCohortMonth} cohort</span>
                  </div>
                )}
                <div className="w-full max-w-sm mx-auto bg-gray-800 rounded-full h-3 mb-4 overflow-hidden">
                  <div className="h-3 rounded-full bg-gradient-to-r from-[#FF3366] to-[#FF6B6B] transition-all duration-500" style={{ width: `${Math.max(2, 100 - liveSpots)}%` }}></div>
                </div>
                <p className="text-white text-base font-bold leading-relaxed">
                  <span className="text-[#FF3366] font-black">We can only onboard 100 accounts per month</span> — that&apos;s 3–4 setups a day, every day.
                </p>
                <p className="text-gray-400 text-sm mt-2">
                  Can&apos;t get you in this month? You go straight to the <strong className="text-white">front of the line</strong> for next month.
                </p>
              </>
            )}
          </div>

          {/* 3 Steps */}
          <div className="mb-20">
            <h3 className="text-center text-3xl font-black uppercase mb-12 text-white">Just Follow <span className="text-[#00FF94]">3 Steps</span></h3>
            <div className="grid md:grid-cols-3 gap-8 relative">
              <div className="hidden md:block absolute top-14 left-[calc(16.67%+1rem)] right-[calc(16.67%+1rem)] h-0.5 bg-gradient-to-r from-[#00FF94]/40 via-[#00FF94] to-[#00FF94]/40 z-0"></div>
              {[
                { num: '01', icon: 'ri-edit-2-line', title: 'Join the Waitlist', desc: "Fill out the short form below. Tell us a little about yourself — no business needed, just curiosity. We'll confirm your spot within 24 hours.", badge: 'Takes 60 seconds' },
                { num: '02', icon: 'ri-settings-3-line', title: 'We Build Your Jessica', desc: 'Our team trains and configures your personal AI voice agent. We handle everything — voice, personality, calendar integration, and call flows.', badge: 'Done in 48 hours' },
                { num: '03', icon: 'ri-rocket-line', title: 'Take Her for a Spin', desc: 'Your Jessica goes live. She answers calls, books appointments, handles cancellations — all to your calendar. No strings, no credit card, no catch.', badge: '100% free trial' },
              ].map((step, idx) => (
                <div key={idx} className="relative z-10 bg-[#1A1A1A] border-2 border-gray-800 hover:border-[#00FF94] transition-all duration-300 rounded-2xl p-8 group">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-[#00FF94] rounded-full shrink-0 shadow-[0_0_20px_rgba(0,255,148,0.4)]">
                      <span className="text-[#0D0D0D] font-black text-lg">{step.num}</span>
                    </div>
                    <div className="w-10 h-10 flex items-center justify-center bg-[#00FF94]/10 rounded-xl group-hover:bg-[#00FF94]/20 transition-colors">
                      <i className={`${step.icon} text-xl text-[#00FF94]`}></i>
                    </div>
                  </div>
                  <h4 className="text-xl font-black text-white mb-3">{step.title}</h4>
                  <p className="text-gray-400 text-sm leading-relaxed mb-5">{step.desc}</p>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#00FF94]/10 rounded-full">
                    <span className="w-1.5 h-1.5 bg-[#00FF94] rounded-full"></span>
                    <span className="text-[#00FF94] text-xs font-semibold">{step.badge}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-[#1A1A1A] border-2 border-[#00FF94] rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(0,255,148,0.1)]">
              <div className="bg-gradient-to-r from-[#00FF94]/20 to-[#0066FF]/10 px-8 py-6 border-b border-gray-800">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h3 className="text-2xl font-black text-white">
                      {liveSpots === 0 ? 'Join the Next Cohort' : 'Claim Your Free Trial'}
                    </h3>
                    <p className="text-gray-400 text-sm mt-1">
                      {liveSpots === 0
                        ? `This month is full — you'll be first in line${liveCohortMonth ? ` for ${liveCohortMonth}` : ' next month'}`
                        : 'Spots are first-come, first-served'}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    {liveSpots === 0 ? (
                      <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-500/40 rounded-full">
                        <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></span>
                        <span className="text-amber-400 text-xs font-bold uppercase tracking-wider">Next cohort open</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-4 py-2 bg-[#FF3366]/20 border border-[#FF3366]/40 rounded-full">
                        <span className="w-2 h-2 bg-[#FF3366] rounded-full animate-pulse"></span>
                        <span className="text-[#FF3366] text-xs font-bold uppercase tracking-wider">{liveSpots} spots left</span>
                      </div>
                    )}
                    <p className="text-gray-600 text-xs">
                      {liveSpots === 0
                        ? liveCohortMonth ? `queuing for ${liveCohortMonth}` : 'next month queue'
                        : 'of 100 this month'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-8 py-8">
                {formSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 flex items-center justify-center bg-[#00FF94]/20 rounded-full mx-auto mb-6">
                      <i className="ri-check-line text-5xl text-[#00FF94]"></i>
                    </div>
                    <h3 className="text-2xl font-black text-white mb-3">You&apos;re On The List! 🎉</h3>

                    {signupResult ? (
                      <div className="space-y-4 mb-6">
                        {/* Cohort badge */}
                        {signupResult.cohortPosition !== null && (
                          <div className="inline-flex items-center gap-3 px-6 py-3 bg-[#00FF94]/10 border border-[#00FF94]/40 rounded-full">
                            <span className="text-[#00FF94] font-black text-2xl">#{signupResult.cohortPosition}</span>
                            <span className="text-white text-sm font-semibold">
                              {signupResult.isCurrentMonth
                                ? 'Your seat this month'
                                : `Your seat in ${signupResult.cohortMonth ?? 'the next cohort'}`}
                            </span>
                          </div>
                        )}

                        {/* Future cohort notice */}
                        {!signupResult.isCurrentMonth && signupResult.cohortMonth && (
                          <div className="bg-[#FF3366]/10 border border-[#FF3366]/40 rounded-xl px-5 py-4 text-left">
                            <div className="flex items-center gap-3">
                              <span className="w-5 h-5 flex items-center justify-center text-[#FF3366] shrink-0">
                                <i className="ri-calendar-event-line text-base"></i>
                              </span>
                              <div>
                                <p className="text-[#FF3366] font-bold text-sm mb-1">This month is full — you&apos;re in {signupResult.cohortMonth}!</p>
                                <p className="text-gray-400 text-xs leading-relaxed">
                                  You&apos;re seat <strong className="text-white">#{signupResult.cohortPosition}</strong> of 100 for {signupResult.cohortMonth}.
                                  {signupResult.spotsRemaining !== null && (
                                    <> There are still <strong className="text-white">{signupResult.spotsRemaining} spots</strong> remaining in that cohort.</>
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Current month spots remaining */}
                        {signupResult.isCurrentMonth && signupResult.spotsRemaining !== null && (
                          <p className="text-gray-500 text-xs">
                            <span className="text-[#FF3366] font-bold">{signupResult.spotsRemaining} spots</span> still remaining this month after your signup.
                          </p>
                        )}

                        {/* API message if present */}
                        {signupResult.message && (
                          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-gray-700 rounded-lg">
                            <i className="ri-information-line text-gray-400 text-sm"></i>
                            <p className="text-gray-300 text-xs font-medium">{signupResult.message}</p>
                          </div>
                        )}
                      </div>
                    ) : waitlistPosition !== null ? (
                      <div className="inline-flex items-center gap-3 px-6 py-3 bg-[#00FF94]/10 border border-[#00FF94]/40 rounded-full mb-4">
                        <span className="text-[#00FF94] font-black text-2xl">#{waitlistPosition}</span>
                        <span className="text-white text-sm font-semibold">Your spot on the waitlist</span>
                      </div>
                    ) : null}

                    <p className="text-gray-400 text-base leading-relaxed max-w-md mx-auto">We&apos;ll confirm your spot within 24 hours and get your Jessica set up. Check your email for next steps.</p>
                  </div>
                ) : (
                  <form data-readdy-form={true} id="jessica-waitlist-form" className="space-y-5" onSubmit={handleSubmit}>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">First Name *</label>
                        <input required placeholder="Jane" className="w-full bg-[#0D0D0D] border border-gray-700 focus:border-[#00FF94] text-white text-sm rounded-lg px-4 py-3 outline-none transition-colors placeholder-gray-600" name="first_name" value={formData.first_name} onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">Last Name *</label>
                        <input required placeholder="Smith" className="w-full bg-[#0D0D0D] border border-gray-700 focus:border-[#00FF94] text-white text-sm rounded-lg px-4 py-3 outline-none transition-colors placeholder-gray-600" name="last_name" value={formData.last_name} onChange={handleInputChange} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Email Address *</label>
                      <input required placeholder="jane@example.com" className="w-full bg-[#0D0D0D] border border-gray-700 focus:border-[#00FF94] text-white text-sm rounded-lg px-4 py-3 outline-none transition-colors placeholder-gray-600" type="email" name="email" value={formData.email} onChange={handleInputChange} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Phone Number</label>
                      <input placeholder="+1 (555) 000-0000" className="w-full bg-[#0D0D0D] border border-gray-700 focus:border-[#00FF94] text-white text-sm rounded-lg px-4 py-3 outline-none transition-colors placeholder-gray-600" type="tel" name="phone" value={formData.phone} onChange={handleInputChange} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">What are you hoping Jessica can help with?</label>
                      <textarea name="use_case" rows={3} maxLength={500} placeholder="e.g. Book appointments for my salon, handle inbound calls for my side hustle, just curious to see how it works..." className="w-full bg-[#0D0D0D] border border-gray-700 focus:border-[#00FF94] text-white text-sm rounded-lg px-4 py-3 outline-none transition-colors placeholder-gray-600 resize-none" value={formData.use_case} onChange={handleInputChange}></textarea>
                      <p className="text-gray-600 text-xs mt-1">{formData.use_case.length}/500 characters. No business required — just tell us what you&apos;re curious about.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">How did you hear about this?</label>
                      <select name="referral_source" className="w-full bg-[#0D0D0D] border border-gray-700 focus:border-[#00FF94] text-white text-sm rounded-lg px-4 py-3 outline-none transition-colors cursor-pointer" value={formData.referral_source} onChange={handleInputChange}>
                        <option value="">Select one...</option>
                        <option value="called_jessica">I just called Jessica</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="referral">Someone referred me</option>
                        <option value="google">Google Search</option>
                        <option value="social_media">Social Media</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <button type="submit" className="w-full py-4 bg-[#00FF94] text-[#0D0D0D] font-extrabold text-base uppercase tracking-wide rounded-lg hover:bg-white transition-all duration-300 shadow-[0_0_30px_rgba(0,255,148,0.3)] hover:shadow-[0_0_50px_rgba(0,255,148,0.5)] hover:-translate-y-0.5 whitespace-nowrap cursor-pointer">
                      Claim My Free Trial →
                    </button>
                    <div className="flex items-center justify-center gap-2 px-5 py-3 bg-[#00FF94]/10 border border-[#00FF94]/30 rounded-xl mt-1">
                      <span className="w-5 h-5 flex items-center justify-center text-[#00FF94] text-xs shrink-0">
                        <i className="ri-verified-badge-line"></i>
                      </span>
                      <p className="text-center text-[#00FF94] text-sm font-semibold">No credit card. No commitment. No catch. Just your own Jessica, free.</p>
                    </div>
                  </form>
                )}
              </div>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-3 gap-4 mt-8">
              {[
                { icon: 'ri-shield-check-line', title: 'Zero Risk Trial', desc: '30 days free, no strings!' },
                { icon: 'ri-time-line', title: '48hr Setup', desc: 'We do the work' },
                { icon: 'ri-calendar-line', title: 'Your Calendar', desc: 'Real bookings' },
              ].map((item, idx) => (
                <div key={idx} className="text-center bg-[#1A1A1A] border border-gray-800 rounded-xl p-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#00FF94]/10 rounded-lg mx-auto mb-2">
                    <i className={`${item.icon} text-xl text-[#00FF94]`}></i>
                  </div>
                  <p className="text-white text-sm font-bold">{item.title}</p>
                  <p className="text-gray-500 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Testimonials */}
            <div className="mt-16">
              <div className="flex items-center justify-center gap-3 mb-8">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent to-gray-700"></div>
                <p className="text-gray-400 text-sm font-semibold uppercase tracking-widest whitespace-nowrap px-4">What people are saying</p>
                <div className="h-px flex-1 bg-gradient-to-l from-transparent to-gray-700"></div>
              </div>
              <div className="space-y-4">
                {[
                  { initials: 'MT', name: 'Marcus T.', role: 'Independent Contractor', location: 'Dallas, TX', text: '"I called Jessica just to see if it was real. She booked me a slot, I cancelled it, she handled it perfectly. I signed up for my own the same day."' },
                  { initials: 'PS', name: 'Priya S.', role: 'Freelance Consultant', location: 'Phoenix, AZ', text: "\"Honestly I thought it was a gimmick. Then she answered my weird follow-up questions without missing a beat. I'm on the waitlist and telling everyone.\"" },
                  { initials: 'DW', name: 'Derek W.', role: 'Small Business Owner', location: 'Nashville, TN', text: '"We were losing 10–15 calls a week to voicemail. Two weeks with our own Jessica and that number is basically zero. The ROI math is embarrassingly obvious."' },
                  { initials: 'CR', name: 'Camille R.', role: 'Salon Owner', location: 'Atlanta, GA', text: "\"My clients love her. She books, reschedules, sends reminders — all without me touching my phone. I genuinely forget she's not a real person sometimes.\"" },
                  { initials: 'JO', name: 'James O.', role: 'Real Estate Agent', location: 'Charlotte, NC', text: "\"I was skeptical. Peter walked me through the demo, I called Jessica myself, and I was sold in under 3 minutes. Best decision I've made for my business this year.\"" },
                ].map((t, idx) => (
                  <div key={idx} className="bg-[#1A1A1A] border border-gray-800 hover:border-gray-600 rounded-2xl p-6 transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 flex items-center justify-center rounded-full shrink-0 bg-[#00FF94]/20 text-[#00FF94] font-black text-sm">{t.initials}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>)}
                        </div>
                        <p className="text-gray-300 text-sm leading-relaxed mb-3">{t.text}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white text-sm font-bold">{t.name}</span>
                          <span className="text-gray-600 text-xs">·</span>
                          <span className="text-gray-500 text-xs">{t.role}</span>
                          <span className="text-gray-600 text-xs">·</span>
                          <span className="text-gray-500 text-xs flex items-center gap-1"><i className="ri-map-pin-line text-[10px]"></i>{t.location}</span>
                        </div>
                      </div>
                      <div className="shrink-0 flex items-center gap-1 px-2.5 py-1 bg-[#00FF94]/10 border border-[#00FF94]/20 rounded-full">
                        <i className="ri-verified-badge-line text-[#00FF94] text-xs"></i>
                        <span className="text-[#00FF94] text-xs font-semibold">Verified</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center justify-center gap-4 py-4 bg-[#1A1A1A] border border-gray-800 rounded-xl">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-amber-400 text-lg"></i>)}
                </div>
                <span className="text-white font-black text-lg">4.9</span>
                <span className="text-gray-500 text-sm">from 47 early testers</span>
                <span className="text-gray-600">·</span>
                <span className="text-[#00FF94] text-sm font-semibold">100% would recommend</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6 bg-[#1A1A1A]">
        <div className="max-w-[900px] mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00FF94]/10 text-[#00FF94] rounded-full text-sm font-bold uppercase tracking-widest mb-6">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-question-answer-line"></i></span>
              Got Questions?
            </div>
            <h2 className="text-4xl md:text-5xl font-black uppercase mb-4">We Heard You. <span className="text-[#00FF94]">Here&apos;s the Truth.</span></h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">No fluff. No fine print. Just straight answers to the questions we get every single day.</p>
          </div>
          <div className="space-y-3">
            {[
              { q: 'Do I need a business to get my own Jessica?', a: "Absolutely not. You don't need a business, a website, or even a business idea. If you're just curious about how AI voice agents work and want to kick the tires — that's more than enough reason. Think of it as a no-strings test drive. We set her up, you play around, no commitment required." },
              { q: 'What calendar does Jessica connect to?', a: "Jessica connects to Google Calendar and can work with most major calendar platforms. During your setup, our team configures the integration for you — you don't touch a single line of code. She'll book, reschedule, and cancel appointments directly to your calendar in real time." },
              { q: 'What happens after the free trial?', a: "Nothing happens automatically — we don't store your card, and there's no auto-renewal. After your trial, if you love Jessica and want to keep her, we'll talk about what a paid plan looks like for your situation. If you don't want to continue, you just walk away. Zero pressure, zero awkward cancellation calls." },
              { q: 'How long does setup actually take?', a: "We target 48 hours from the moment you're confirmed on the waitlist. Our team handles the voice training, calendar integration, and call flow configuration. You'll get a notification when she's live, along with a dedicated phone number to test her out." },
              { q: 'Can Jessica handle cancellations and reschedules — not just bookings?', a: "Yes — that's actually one of the best things to test. Call her, book an appointment, then call back and cancel it. She handles the full lifecycle: booking, confirmation, rescheduling, and cancellation. That's exactly what the demo is designed to show you." },
              { q: "What if I don't get a spot this month?", a: "We can only onboard 3–4 accounts per day, so some months fill up fast. If we can't get you in this month, you automatically go to the front of the line for next month. We'll email you to confirm your priority spot — you won't lose your place." },
              { q: "Is this really free? What's the catch?", a: "It's genuinely free. No credit card, no hidden fees, no \"free trial that converts to $299/month without warning.\" The catch — if you can call it that — is that we hope you love it enough to become a paying customer. But that's entirely your call, after you've seen it work." },
            ].map((faq, idx) => (
              <div key={idx} className={`border rounded-xl overflow-hidden transition-all duration-300 ${expandedFaq === idx ? 'border-[#00FF94] bg-[#0D0D0D]' : 'border-gray-800 bg-[#0D0D0D] hover:border-gray-600'}`}>
                <button onClick={() => toggleFaq(idx)} className="w-full flex items-center justify-between gap-4 px-7 py-5 text-left cursor-pointer group">
                  <span className={`text-base font-bold leading-snug transition-colors ${expandedFaq === idx ? 'text-[#00FF94]' : 'text-white group-hover:text-[#00FF94]'}`}>{faq.q}</span>
                  <span className={`w-8 h-8 flex items-center justify-center rounded-full shrink-0 transition-all duration-300 ${expandedFaq === idx ? 'bg-[#00FF94] text-[#0D0D0D] rotate-45' : 'bg-gray-800 text-gray-400 group-hover:bg-gray-700'}`}>
                    <i className="ri-add-line text-lg"></i>
                  </span>
                </button>
                <div className={`transition-all duration-300 ease-in-out overflow-hidden ${expandedFaq === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="px-7 pb-6">
                    <div className="h-px bg-gray-800 mb-5"></div>
                    <p className="text-gray-300 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-gray-500 text-sm mb-4">Still have a question we didn&apos;t answer?</p>
            <a href="https://petergits.com/#contact" rel="nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-transparent border border-gray-700 hover:border-[#00FF94] text-gray-400 hover:text-[#00FF94] text-sm font-semibold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-mail-send-line"></i></span>
              Ask Peter directly
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-6 bg-[#1A1A1A]">
        <div className="max-w-[1000px] mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-black uppercase mb-8">Stop Losing Money<br /><span className="text-[#00FF94]">Start Today</span></h2>
          <p className="text-2xl text-gray-400 mb-12 max-w-3xl mx-auto">Every day you wait is another $2,000+ walking out the door. Your competitors aren&apos;t waiting.</p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
            <a href="#get-jessica" onClick={(e) => { e.preventDefault(); scrollToForm(); }} className="px-14 py-6 bg-[#00FF94] text-[#0D0D0D] text-lg font-extrabold uppercase tracking-wide hover:bg-white transition-all duration-300 shadow-[0_0_30px_rgba(0,255,148,0.3)] hover:shadow-[0_0_50px_rgba(0,255,148,0.5)] hover:-translate-y-0.5 whitespace-nowrap cursor-pointer inline-block">
              Get My Free Jessica →
            </a>
            <a href="#demo" onClick={(e) => { e.preventDefault(); scrollToDemo(); }} className="px-14 py-6 bg-transparent text-white text-lg font-extrabold uppercase tracking-wide border-2 border-white hover:bg-white hover:text-[#0D0D0D] transition-all duration-300 whitespace-nowrap cursor-pointer inline-block">
              Learn More
            </a>
          </div>
          <div className="grid md:grid-cols-3 gap-8 text-left">
            {[
              { icon: 'ri-timer-flash-line', title: 'Setup in 48 Hours', desc: 'No contract, go live in 2-5 days' },
              { icon: 'ri-shield-check-line', title: 'Zero Risk Trial', desc: '30 days free, no strings!' },
              { icon: 'ri-customer-service-line', title: 'White-Glove Support', desc: 'We handle everything, you just watch it work' },
            ].map((item, idx) => (
              <div key={idx} className="bg-[#0D0D0D] border border-gray-800 p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-[#00FF94]/10 rounded-lg mb-4">
                  <i className={`${item.icon} text-2xl text-[#00FF94]`}></i>
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#0D0D0D] border-t border-gray-800">
        <div className="max-w-[1400px] mx-auto text-center">
          <p className="text-gray-500 mb-4">© 2025 GITS. All rights reserved.</p>
          <div className="flex justify-center gap-6">
            <a href="https://petergits.com" rel="nofollow" className="text-gray-400 hover:text-[#00FF94] transition-colors cursor-pointer">Portfolio</a>
            <a href="https://petergits.com/#contact" rel="nofollow" className="text-gray-400 hover:text-[#00FF94] transition-colors cursor-pointer">Contact</a>
            <a href="https://readdy.ai/?ref=logo" rel="nofollow" className="text-gray-400 hover:text-[#00FF94] transition-colors cursor-pointer">Powered by Readdy</a>
          </div>
        </div>
      </footer>

      {/* Floating CTA */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
        <a href="#get-jessica" onClick={(e) => { e.preventDefault(); scrollToForm(); }} className="flex items-center gap-3 px-8 py-4 bg-[#00FF94] text-[#0D0D0D] font-extrabold text-sm uppercase tracking-widest rounded-full shadow-[0_0_40px_rgba(0,255,148,0.5)] hover:bg-white hover:text-[#FF3366] hover:shadow-[0_0_60px_rgba(0,255,148,0.6)] hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap cursor-pointer">
          <span className="w-2 h-2 bg-[#0D0D0D] rounded-full animate-pulse"></span>
          Claim My Spot — Free Trial
          <span className="w-5 h-5 flex items-center justify-center"><i className="ri-arrow-up-line text-base"></i></span>
        </a>
      </div>

    </div>
  );
}
